'use client';

import React from 'react';
import { OrderSuccess } from '@/ui/components/pages/order-received/order-success/order-success';
import { useOrderReceivedData } from '@/ui/providers/order-received-data-provider';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer/RichTextRenderer';
import { RichTextFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { UniversalPaymentStatusEnum } from 'typescript-node-phoenix-payments/dist/model/universalPaymentStatusEnum';

interface SingleLineField {
  text?: string | null;
}

/**
 * CMS fields for the order-confirmation payment-status tile. The ids come straight from the
 * Crystallize shape. Only the success copy is editable here; the failure state delegates to the shared
 * PaymentFailed component, which owns its own (localized) copy and the retry/cancel flow.
 */
export interface OrderConfirmationPaymentStatusTile {
  'success-title'?: SingleLineField | null;
  'success-text'?: RichTextFragment | null;
  'cta-label'?: SingleLineField | null;
  'cta-link'?: SingleLineField | null;
}

/**
 * The order-confirmation "thank you" / payment-status tile. It renders the two payment states the page
 * has always had, so a single untagged tile can be placed on the modular page:
 * - success (a paid/authorized/open/pending payment): the OrderSuccess header with the real order data,
 *   layering the editable CMS copy on top (each field falls back to the default localized copy when the
 *   editor leaves it empty). OrderSuccess itself shows the pending badge from `paymentStatus`.
 * - failure: the shared PaymentFailed component (retry/cancel), pre-rendered by the shell and handed
 *   down as `paymentFailedContent` (the same slot pattern the order-summary tile uses).
 */
export function OrderConfirmationPaymentStatus({
  tile,
}: Readonly<{ tile?: OrderConfirmationPaymentStatusTile }>) {
  const data = useOrderReceivedData();

  if (!data) {
    return null;
  }

  const isSuccess =
    data.paymentStatus === UniversalPaymentStatusEnum.Paid ||
    data.paymentStatus === UniversalPaymentStatusEnum.Authorized ||
    data.paymentStatus === UniversalPaymentStatusEnum.Open ||
    data.paymentStatus === UniversalPaymentStatusEnum.Pending;

  if (!isSuccess) {
    return data.paymentFailedContent ?? null;
  }

  const textField = tile?.['success-text'];

  const body = textField?.json?.length ? (
    <RichTextRenderer blocks={textField} className="mt-2" />
  ) : undefined;

  return (
    <OrderSuccess
      orderReference={data.orderReference}
      orderId={data.orderId}
      isOrderVirtual={data.isVirtual}
      paymentStatus={data.paymentStatus}
      title={tile?.['success-title']?.text ?? undefined}
      body={body}
      ctaLabel={tile?.['cta-label']?.text ?? undefined}
      ctaHref={tile?.['cta-link']?.text ?? undefined}
    />
  );
}
